import React, { useState } from "react";
import { uploadPayout, deletePayout } from "../../../../Api/apiUtils";
import { Upload, Trash2 } from "react-feather";
import { Page, PageHeader, Card, Button, Field, Select, TextInput, useConfirm } from "../../../../ui";
import "./teacherPayout.scss";
import { toast } from "react-toastify";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";

const TeacherPayout = () => {
  const currentYear = new Date().getFullYear();
  const months = [
    { name: "January", value: "01" },
    { name: "February", value: "02" },
    { name: "March", value: "03" },
    { name: "April", value: "04" },
    { name: "May", value: "05" },
    { name: "June", value: "06" },
    { name: "July", value: "07" },
    { name: "August", value: "08" },
    { name: "September", value: "09" },
    { name: "October", value: "10" },
    { name: "November", value: "11" },
    { name: "December", value: "12" },
  ];

  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedMonth, setSelectedMonth] = useState("01");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { confirm, dialog } = useConfirm();

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedYear(currentYear.toString()); // Always keep it current year
  };
  

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(event.target.value);
  };

  const getDateRange = () => {
    const year = parseInt(selectedYear, 10);
    const month = parseInt(selectedMonth, 10);

    if (isNaN(year) || isNaN(month) || month < 1 || month > 12) {
      toast.error("Invalid date selection.");
      return { fromDate: "", toDate: "" };
    }

    const fromDate = new Date(year, month - 1, 1).toISOString().split("T")[0];
    const toDate = new Date(year, month, 0).toISOString().split("T")[0];

    console.log("From Date:", fromDate, "To Date:", toDate);

    return { fromDate, toDate };
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload.");
      return;
    }

    const { fromDate, toDate } = getDateRange();
    if (!fromDate || !toDate) return;

    setLoading(true);
    try {
      await uploadPayout(file, fromDate, toDate);
      toast.success("Payout uploaded successfully");
      trackEvent(MixpanelEvents.TEACHER_PAYOUT_UPLOADED, { fromDate, toDate });
    } catch (error) {
      toast.error("Error uploading payout");
      trackEvent(MixpanelEvents.TEACHER_PAYOUT_UPLOAD_FAILED, { fromDate, toDate });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayout = async () => {
    const { fromDate, toDate } = getDateRange();
    if (!fromDate || !toDate) return;

    const monthName = months.find((m) => m.value === selectedMonth)?.name ?? selectedMonth;
    const ok = await confirm({
      title: "Delete payout?",
      message: `The uploaded payout for ${monthName} ${selectedYear} will be deleted.`,
      confirmLabel: "Delete payout",
      tone: "danger",
    });
    if (!ok) return;

    setLoading(true);
    try {
      await deletePayout(fromDate, toDate);
      toast.success("Payout deleted successfully");
      trackEvent(MixpanelEvents.TEACHER_PAYOUT_DELETED, { fromDate, toDate });
    } catch (error) {
      toast.error("Error deleting payout");
      trackEvent(MixpanelEvents.TEACHER_PAYOUT_DELETE_FAILED, { fromDate, toDate });
    } finally {
      setLoading(false);
    }
  };

  const monthOptions = [{ value: "", label: "Select month" }, ...months.map((m) => ({ value: m.value, label: m.name }))];

  return (
    <Page>
      <PageHeader
        eyebrow="Teacher Operations"
        title="Teacher Payout"
        subtitle="Upload or remove the payout sheet for a month."
      />

      <Card className="teacher-payout-card">
        <div className="teacher-payout-form">
          <Field label="Year" hint="Payouts can only be uploaded for the current year.">
            {(ctl) => (
              <Select {...ctl} options={[{ value: String(currentYear), label: String(currentYear) }]} value={selectedYear} disabled />
            )}
          </Field>
          <Field label="Month">
            {(ctl) => <Select {...ctl} options={monthOptions} value={selectedMonth} onChange={handleMonthChange} />}
          </Field>
          <Field label="Payout file">
            {(ctl) => <TextInput {...ctl} type="file" className="ds-input--file" onChange={handleFileChange} />}
          </Field>
          <div className="teacher-payout-actions">
            <Button variant="ghost" className="teacher-payout-delete" icon={<Trash2 size={16} aria-hidden />} onClick={handleDeletePayout} disabled={loading}>
              Delete payout
            </Button>
            <Button variant="primary" icon={<Upload size={16} aria-hidden />} onClick={handleUpload} loading={loading}>
              Upload payout
            </Button>
          </div>
        </div>
      </Card>
      {dialog}
    </Page>
  );
};

export default TeacherPayout;
